import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobStatusResponse, ProcessingStep, WebSocketEvent } from '../types/api';
import { getJobStatus } from '../services/api';
import { JobWebSocket } from '../services/websocket';

interface UseJobStatusResult {
  status: JobStatusResponse | null;
  isLoading: boolean;
  error: string | null;
  isConnected: boolean;
}

export function useJobStatus(jobId: string): UseJobStatusResult {
  const [status, setStatus] = useState<JobStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsRef = useRef<JobWebSocket | null>(null);
  const wsFailed = useRef(false);

  const startPolling = useCallback(() => {
    pollingRef.current = setInterval(async () => {
      try {
        const data = await getJobStatus(jobId);
        setStatus(data);
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          if (pollingRef.current) clearInterval(pollingRef.current);
        }
      } catch {
        // Polling errors are non-fatal
      }
    }, 2_000);
  }, [jobId]);

  const handleWsEvent = useCallback((event: WebSocketEvent) => {
    setStatus((prev) => {
      if (!prev) return prev;
      const updated = { ...prev };
      if (event.progress_pct !== undefined) updated.progress_pct = event.progress_pct;
      if (event.status) updated.status = event.status as JobStatusResponse['status'];
      if (event.event_type === 'step_started' || event.event_type === 'step_completed') {
        updated.processing_steps = updated.processing_steps.map((step: ProcessingStep) => {
          if (step.step_name === event.step) {
            return {
              ...step,
              status: event.event_type === 'step_completed' ? 'done' : 'running',
            };
          }
          return step;
        });
      }
      return updated;
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const initial = await getJobStatus(jobId);
        if (mounted) {
          setStatus(initial);
          setIsLoading(false);
        }
      } catch (e) {
        if (mounted) setError('Failed to load job status');
        setIsLoading(false);
        return;
      }

      const ws = new JobWebSocket(
        jobId,
        handleWsEvent,
        (code) => {
          setIsConnected(false);
          if (code !== 1000 && !wsFailed.current) {
            wsFailed.current = true;
            startPolling();
          }
        }
      );
      wsRef.current = ws;
      ws.connect();
      setIsConnected(true);
    };

    init();

    return () => {
      mounted = false;
      wsRef.current?.disconnect();
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [jobId, handleWsEvent, startPolling]);

  return { status, isLoading, error, isConnected };
}
